import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CheckCircle2, LifeBuoy, Loader2, Mail, MessageSquareText, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SupportPage (/assistenza) — anonymous "help me" form for users who
 * can't log in or need to reach the operator. Posts to
 * `POST /api/support/request` which validates + rate-limits + emails.
 */
export default function SupportPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ kind: 'idle' }); // idle | success | error

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const descriptionValid = description.trim().length >= 10;
  const canSubmit = emailValid && descriptionValid && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setStatus({ kind: 'idle' });
    try {
      await axios.post(`${API}/support/request`, {
        email: email.trim(),
        subject: subject.trim(),
        description: description.trim(),
      });
      setStatus({ kind: 'success' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        kind: 'error',
        message:
          typeof detail === 'string'
            ? detail
            : "Si è verificato un problema durante l'invio della richiesta. Riprova tra qualche minuto.",
      });
    } finally {
      setLoading(false);
    }
  };

  const returnToLogin = () => navigate('/');

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="support-page">
      <Header showSearch={false} />

      <main className="flex-1 w-full max-w-2xl mx-auto px-4 py-6">
        <button
          type="button"
          onClick={returnToLogin}
          className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-700 text-sm mb-4"
          data-testid="support-back"
        >
          <ArrowLeft className="w-4 h-4" /> Torna al login
        </button>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8">
          {status.kind === 'success' ? (
            <div className="text-center py-6" data-testid="support-success">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Richiesta inviata</h1>
              <p className="text-slate-600 max-w-md mx-auto mb-6">
                La tua richiesta è stata inviata correttamente. Ti contatteremo
                all&apos;indirizzo email indicato il prima possibile.
              </p>
              <Button
                onClick={returnToLogin}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl"
                data-testid="support-back-to-login"
              >
                Torna al login
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <LifeBuoy className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Richiedi assistenza</h1>
                  <p className="text-slate-600 text-sm mt-1">
                    Compila il modulo qui sotto. Ti risponderemo il prima possibile.
                  </p>
                </div>
              </div>

              <form onSubmit={submit} className="space-y-4" noValidate>
                <div>
                  <Label htmlFor="support-email" className="text-slate-700">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="support-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Inserisci la tua email"
                      required
                      autoComplete="email"
                      className="pl-9 rounded-xl bg-white border-slate-300"
                      data-testid="support-email-input"
                    />
                  </div>
                  {email && !emailValid && (
                    <p className="text-xs text-red-600 mt-1">Formato email non valido.</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="support-subject" className="text-slate-700">
                    Oggetto <span className="text-slate-400 font-normal">(facoltativo)</span>
                  </Label>
                  <Input
                    id="support-subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Es. Problema di accesso"
                    maxLength={200}
                    className="mt-1 rounded-xl bg-white border-slate-300"
                    data-testid="support-subject-input"
                  />
                </div>

                <div>
                  <Label htmlFor="support-description" className="text-slate-700">
                    Descrivi il problema <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative mt-1">
                    <MessageSquareText className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Textarea
                      id="support-description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Spiega nel dettaglio cosa è successo e come possiamo aiutarti."
                      required
                      rows={6}
                      maxLength={5000}
                      className="pl-9 pt-3 rounded-xl bg-white border-slate-300 resize-y min-h-[140px]"
                      data-testid="support-description-input"
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1 text-xs">
                    {description && !descriptionValid ? (
                      <span className="text-red-600">Minimo 10 caratteri.</span>
                    ) : (
                      <span className="text-slate-400">Minimo 10 caratteri.</span>
                    )}
                    <span className="text-slate-400">{description.length}/5000</span>
                  </div>
                </div>

                {status.kind === 'error' && (
                  <div
                    className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3"
                    data-testid="support-error"
                    role="alert"
                  >
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-800">Invio non riuscito</p>
                      <p className="text-sm text-red-700 mt-1">{status.message}</p>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={!canSubmit}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl"
                    data-testid="support-submit"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Invia richiesta
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={returnToLogin}
                    className="flex-1 rounded-xl border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid="support-cancel"
                  >
                    Annulla
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
